import { useEffect } from 'react';
import type React from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useKnownAvailableParams } from '@change-corgi/core/react/router';

import { ImageUploadProvider } from 'src/app/shared/hooks/imageUpload';
import { useToast } from 'src/app/shared/toast';

import { useAddEndorsement } from '../../hooks/useAddEndorsement';
import { EndorsementForm } from '../EndorsementForm';

import { contentContainer, formSection, pageContainer, previewSection } from './AddEndorsement.styles';

const AddEndorsementContent: React.FC<{ petitionSlug: string }> = ({ petitionSlug }) => {
	const { translate } = useI18n();
	const { data, actions, uploader } = useAddEndorsement({ petitionSlug });
	const { showToast } = useToast();

	// Show error toast when error occurs
	useEffect(() => {
		if (data.error) {
			showToast({
				variant: 'error',
				duration: 5000,
				message: data.error.message,
			});
			actions.clearError();
		}
	}, [data.error, showToast, actions]);

	return (
		<EndorsementForm
			title={translate('corgi.petition.starter_dashboard_v2.endorsements.add.page_title')}
			backButtonAriaLabel={translate('corgi.petition.starter_dashboard_v2.endorsements.add.back_button_aria_label')}
			formData={data.formData}
			statementCharCount={data.statementCharCount}
			statementMaxLength={data.statementMaxLength}
			isFormValid={data.formData.name.trim().length > 0 && data.formData.title.trim().length > 0}
			isSubmitting={data.isSubmitting}
			isHelpModalOpen={data.isHelpModalOpen}
			isSubmitModalOpen={data.isSubmitModalOpen}
			onBack={actions.handleBack}
			onFieldChange={actions.handleFieldChange}
			onNext={actions.handleNext}
			onHelpClick={actions.handleHelpClick}
			onCloseHelpModal={actions.closeHelpModal}
			onCloseSubmitModal={actions.closeSubmitModal}
			onConfirmSubmit={actions.confirmSubmit}
			pageContainer={pageContainer()}
			contentContainer={contentContainer()}
			formSection={formSection()}
			previewSection={previewSection()}
			testId="add-endorsement-page"
			uppy={uploader.uppy}
			image={data.image}
		/>
	);
};

export const AddEndorsement: React.FC = () => {
	const { slug } = useKnownAvailableParams<{ slug: string }>();

	return (
		<ImageUploadProvider>
			<AddEndorsementContent petitionSlug={slug} />
		</ImageUploadProvider>
	);
};
